import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "376",
  "slug": "what-is-the-ring-of-fire",
  "title": "What Is the Ring of Fire? Earth's Most Volcanic Region, Explained",
  "excerpt": "A horseshoe of volcanoes and earthquakes wraps around the Pacific Ocean. Here's what the Ring of Fire is, why it exists, and how to spot it on a map.",
  "date": "2026-06-08",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1619266465172-02a857c3556d?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx2b2xjYW5vJTIwZXJ1cHRpb258ZW58MXwwfHx8MTc4MDkwNjI4N3ww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "The Ring of Fire is a horseshoe-shaped belt around the edges of the Pacific Ocean where most of the world's volcanoes and earthquakes occur. It forms along the boundaries of tectonic plates, where oceanic crust dives beneath other plates in a process called subduction.",
  "content": [
    {
      "type": "paragraph",
      "text": "If you marked every major volcano and earthquake on a world map, a pattern would jump out immediately: a vast, broken horseshoe tracing the rim of the Pacific Ocean. This is the Ring of Fire, the single most geologically active region on the planet, and it's home to a large share of the world's volcanoes and earthquakes."
    },
    {
      "type": "heading",
      "text": "Where It Is"
    },
    {
      "type": "paragraph",
      "text": "The Ring of Fire runs for roughly 40,000 kilometres around the Pacific. Trace it from the southern tip of South America up the Andes, along the west coast of North America, across to Alaska's Aleutian Islands, then down through Japan, the Philippines, Indonesia, and on to New Zealand. It isn't a perfect circle — it's an open horseshoe — but the loop is unmistakable."
    },
    {
      "type": "paragraph",
      "text": "Some of the most famous volcanoes and seismic zones on Earth sit along it: Mount Fuji in Japan, Mount St. Helens in the United States, Krakatoa and Mount Tambora in Indonesia, and the long chain of Andean peaks in South America."
    },
    {
      "type": "heading",
      "text": "Why It Exists: Plate Tectonics"
    },
    {
      "type": "paragraph",
      "text": "The Ring of Fire exists because the Earth's surface is broken into enormous moving slabs called tectonic plates. The Pacific Ocean sits on top of several of them, and around its edges those plates collide with the continental plates surrounding the ocean."
    },
    {
      "type": "paragraph",
      "text": "At most of these boundaries, the denser oceanic plate slides beneath the lighter continental plate — a process called subduction. As the descending plate sinks into the hot mantle, it heats up and releases water, which lowers the melting point of the surrounding rock and generates magma. That magma rises to form chains of volcanoes parallel to the boundary. The grinding of the plates against each other, meanwhile, produces frequent and sometimes enormous earthquakes."
    },
    {
      "type": "heading",
      "text": "Why the Numbers Are So Lopsided"
    },
    {
      "type": "paragraph",
      "text": "Geologists often note that something like three-quarters of the world's active and dormant volcanoes and the large majority of its strongest earthquakes occur in or near the Ring of Fire. That concentration isn't coincidence — it's a direct result of how many active plate boundaries are packed around a single ocean."
    },
    {
      "type": "list",
      "items": [
        "Subduction zones, where oceanic crust dives down and feeds volcanoes above.",
        "Deep ocean trenches, the deepest places on Earth, that mark where plates plunge.",
        "Volcanic island arcs, like Japan and the Aleutians, built up by repeated eruptions.",
        "Megathrust faults, capable of producing the planet's most powerful earthquakes and tsunamis."
      ]
    },
    {
      "type": "heading",
      "text": "Living on the Ring"
    },
    {
      "type": "paragraph",
      "text": "Hundreds of millions of people live within reach of the Ring of Fire, in countries like Japan, Indonesia, Chile, and the western United States. That brings real hazards — eruptions, earthquakes, and tsunamis — but also benefits. Volcanic soils are extraordinarily fertile, geothermal energy is abundant, and many of these regions have invested heavily in some of the world's best early-warning and building-safety systems."
    },
    {
      "type": "heading",
      "text": "Some of History's Biggest Events Happened Here"
    },
    {
      "type": "paragraph",
      "text": "The Ring's restlessness has shaped human history. The 1883 eruption of Krakatoa in Indonesia was heard thousands of kilometres away; the 1980 eruption of Mount St. Helens reshaped a corner of Washington State; and the 2011 Tōhoku earthquake off Japan triggered a devastating tsunami. These weren't freak accidents — they're the predictable consequence of living on one of the planet's most active plate boundaries."
    },
    {
      "type": "heading",
      "text": "It Isn't the Only Volcanic Zone"
    },
    {
      "type": "paragraph",
      "text": "The Ring of Fire gets the headlines, but not every volcano belongs to it. Hawaii sits in the middle of the Pacific Plate, far from any boundary, fed instead by a 'hotspot' — a plume of heat rising from deep in the mantle. Iceland straddles the Mid-Atlantic Ridge, where plates pull apart rather than collide. Knowing the difference helps you read why volcanoes appear where they do."
    },
    {
      "type": "heading",
      "text": "How to Spot It From Above"
    },
    {
      "type": "paragraph",
      "text": "From satellite imagery, the fingerprints of the Ring of Fire are everywhere along the Pacific rim: the near-perfect cones of stratovolcanoes, long mountain chains running parallel to coastlines, and arcs of islands strung out in gentle curves. Spot a symmetrical, snow-capped cone near a Pacific coast and you've almost certainly found a piece of the Ring."
    },
    {
      "type": "paragraph",
      "text": "These volcanic signatures are a gift for location guessing — they instantly narrow the world down to a handful of regions. Test your eye for them in a round of EarthGuessr."
    }
  ]
};
